import React, { useState, useEffect } from 'react';
import { getApiEndpoint } from '../../../../config/api';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

function addMonths(date, months) {
  const d = new Date(date);
  const day = d.getDate();
  d.setMonth(d.getMonth() + months);
  if (d.getDate() < day) d.setDate(0);
  return d;
}

function calculateNextTestDate(type, lastDate) {
  if (!lastDate) return '';
  if (type === 'Rope Access') {
    return addMonths(lastDate, 6).toISOString().split('T')[0];
  }
  const d = new Date(lastDate);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

const EditAnchorPointModal = ({ isOpen, onClose, anchorPoint, onSuccess }) => {
  const [formData, setFormData] = useState({
    id: anchorPoint.id,
    name: anchorPoint.name || '',
    location_description: anchorPoint.location_description || anchorPoint.location || '',
    type: anchorPoint.type || 'Fall Arrest',
    notes: anchorPoint.notes || '',
    last_inspection_date: anchorPoint.last_inspection_date ? anchorPoint.last_inspection_date.split('T')[0] : '',
    next_inspection_date: anchorPoint.next_inspection_date ? anchorPoint.next_inspection_date.split('T')[0] : '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData({
      id: anchorPoint.id,
      name: anchorPoint.name || '',
      location_description: anchorPoint.location_description || anchorPoint.location || '',
      type: anchorPoint.type || 'Fall Arrest',
      notes: anchorPoint.notes || '',
      last_inspection_date: anchorPoint.last_inspection_date ? anchorPoint.last_inspection_date.split('T')[0] : '',
      next_inspection_date: anchorPoint.next_inspection_date ? anchorPoint.next_inspection_date.split('T')[0] : '',
    });
  }, [anchorPoint]);

  useEffect(() => {
    setFormData(f => ({
      ...f,
      next_inspection_date: calculateNextTestDate(f.type, f.last_inspection_date)
    }));
  }, [formData.type, formData.last_inspection_date]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.location_description.trim()) {
      setError('ID and Location Description are required.');
      return;
    }
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      const response = await fetch(getApiEndpoint(`/api/properties/${anchorPoint.property_id}/anchor-points/${anchorPoint.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: formData.name,
          location_description: formData.location_description,
          type: formData.type,
          notes: formData.notes,
          last_inspection_date: formData.last_inspection_date,
          next_inspection_date: formData.next_inspection_date
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update anchor point');
      }

      const updatedPoint = await response.json();
      toast.success('Anchor point updated successfully');
      onSuccess({
        ...updatedPoint,
        lastTestDate: updatedPoint.last_inspection_date,
        nextTestDate: updatedPoint.next_inspection_date,
        location: updatedPoint.location_description
      });
    } catch (err) {
      console.error('Error updating anchor point:', err);
      toast.error(err.message || 'Failed to update anchor point');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="rs-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="rs-modal" role="dialog" aria-modal="true" aria-labelledby="edit-ap-title">
        <div className="rs-modal-header">
          <h2 id="edit-ap-title">Edit Anchor Point</h2>
          <button className="rs-modal-close" onClick={onClose} aria-label="Close modal">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="rs-modal-body">
            {error && <div className="rs-error-alert" style={{ marginBottom: 12 }}>{error}</div>}

            <div className="rs-form-grid">
              <div className="rs-form-group">
                <label htmlFor="edit-ap-id">ID</label>
                <input
                  id="edit-ap-id"
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Enter anchor point ID"
                />
              </div>

              <div className="rs-form-group">
                <label htmlFor="edit-ap-type">Type</label>
                <select
                  id="edit-ap-type"
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                  required
                  disabled={!!formData.last_inspection_date}
                >
                  <option value="Rope Access">Rope Access</option>
                  <option value="Fall Arrest">Fall Arrest</option>
                  <option value="Restraint">Restraint</option>
                </select>
              </div>

              <div className="rs-form-group rs-full-width">
                <label htmlFor="edit-ap-location">Location Description</label>
                <input
                  id="edit-ap-location"
                  type="text"
                  value={formData.location_description}
                  onChange={e => setFormData({ ...formData, location_description: e.target.value })}
                  required
                  placeholder="Enter location description"
                />
              </div>

              <div className="rs-form-group">
                <label htmlFor="edit-ap-last-date">Last Inspection Date</label>
                <input id="edit-ap-last-date" type="date" value={formData.last_inspection_date} readOnly />
              </div>

              <div className="rs-form-group">
                <label htmlFor="edit-ap-next-date">Next Test Date</label>
                <input id="edit-ap-next-date" type="date" value={formData.next_inspection_date} readOnly />
              </div>

              <div className="rs-form-group rs-full-width">
                <label htmlFor="edit-ap-notes">Notes</label>
                <textarea
                  id="edit-ap-notes"
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Enter any additional notes"
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="rs-modal-footer">
            <button type="button" className="rs-btn rs-btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="rs-btn rs-btn-primary">Save Changes</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAnchorPointModal;
